import type { MouseEvent } from 'react'
import { isDesktop } from './platform.ts'

/**
 * Abre um endereço externo (http/https) no NAVEGADOR PADRÃO do sistema.
 *
 * No navegador, um `<a target="_blank">` já resolve. No Orbind Desktop, não: o
 * link em nova aba fica preso na webview e nunca chega ao Chrome/Edge de quem
 * clicou. Lá o pedido vai para o plugin opener do Tauri (`opener:default` na
 * capability da janela principal, que só libera http/https/mailto/tel).
 *
 * A chamada usa `__TAURI_INTERNALS__` direto, sem `@tauri-apps/plugin-opener`,
 * pelo mesmo motivo de `features/app/desktop.ts`: o bundle é o mesmo da web.
 */

type Invoke = (comando: string, argumentos?: Record<string, unknown>) => Promise<unknown>

function invokeDoDesktop(): Invoke | null {
  if (!isDesktop()) return null
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }
  return w.__TAURI_INTERNALS__?.invoke ?? null
}

/** Só http/https: o texto veio de outra pessoa e o destino é o sistema. */
function enderecoSeguro(url: string): string | null {
  try {
    const u = new URL(url)
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null
  } catch {
    return null
  }
}

export async function abrirNoNavegadorPadrao(url: string): Promise<void> {
  const seguro = enderecoSeguro(url)
  if (!seguro) return
  const invoke = invokeDoDesktop()
  if (invoke) {
    try {
      await invoke('plugin:opener|open_url', { url: seguro })
      return
    } catch (erro) {
      // janela sem a permissão (ex.: janela extra, fora da capability "main"):
      // melhor abrir dentro do app do que o clique não fazer nada
      console.error('[link externo] opener falhou:', erro)
    }
  }
  window.open(seguro, '_blank', 'noopener,noreferrer')
}

/**
 * `onClick` (e `onAuxClick`, o botão do meio) de um `<a href target="_blank">`
 * externo. No navegador não faz nada — o próprio link abre a aba nova; no
 * desktop desvia para o navegador padrão.
 */
export function aoClicarLinkExterno(evento: MouseEvent<HTMLAnchorElement>): void {
  if (!isDesktop()) return
  // auxclick também dispara com o botão direito; esse é do menu de contexto
  if (evento.type === 'auxclick' && evento.button !== 1) return
  evento.preventDefault()
  void abrirNoNavegadorPadrao(evento.currentTarget.href)
}
